import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSubscriptionPlans, SubscriptionPlan } from '../services/credits';

export default function TermsOfServicePage() {
  const [stripePlans, setStripePlans] = useState<Record<string, SubscriptionPlan>>({});
  const [loadingPlans, setLoadingPlans] = useState(true);

  // Fetch Stripe plan prices on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoadingPlans(true);
        const response = await getSubscriptionPlans();
        const plansMap: Record<string, SubscriptionPlan> = {};
        response.plans.forEach((plan) => {
          plansMap[plan.id] = plan;
        });
        setStripePlans(plansMap);
      } catch (err) {
        console.error('Failed to fetch Stripe plans:', err);
        // Continue with generic text if fetch fails
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  return (
    <div className="min-h-screen bg-bg text-white">
      <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
        <div className="mb-8">
          <Link 
            to="/" 
            className="text-blue-400 hover:text-blue-300 transition-colors inline-flex items-center gap-2 mb-6"
          >
            ← Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
          <p className="text-white/60 text-sm">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8 text-white/80">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Agreement to Terms</h2>
            <p>
              By accessing or using GenAnything ("we," "our," or "us"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these Terms, then you may not access the Service.
            </p>
            <p className="mt-4">
              These Terms apply to all visitors, users, and others who access or use the Service. Your use of the Service is also governed by our Privacy Policy, which is incorporated by reference into these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
            <p>
              GenAnything is an AI-powered platform that enables users to generate videos from images, edit images, and create content using artificial intelligence technology. Our Service includes:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Image-to-video generation using AI models</li>
              <li>Image editing and processing tools</li>
              <li>Template-based video creation</li>
              <li>Cloud storage for uploaded and generated content</li>
              <li>Subscription-based access to premium features</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. User Accounts</h2>
            
            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">3.1 Account Creation</h3>
            <p>
              To use certain features of our Service, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information to keep it accurate</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">3.2 Account Eligibility</h3>
            <p>
              You must be at least 13 years old (or the age of majority in your jurisdiction) to use our Service. By using the Service, you represent and warrant that you meet this age requirement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. Subscription Plans and Payment</h2>
            
            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">4.1 Subscription Plans</h3>
            <p>
              We offer various subscription plans with different features and credit allocations. Plan details, pricing, and features are subject to change. Current plans include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              {loadingPlans ? (
                <li>Loading plan information...</li>
              ) : (
                <>
                  {stripePlans.starter && (
                    <li>
                      <strong>Basic:</strong> ${stripePlans.starter.price_monthly_usd.toFixed(0)}/month - {stripePlans.starter.credits} credits per month
                    </li>
                  )}
                  {stripePlans.pro && (
                    <li>
                      <strong>Pro:</strong> ${stripePlans.pro.price_monthly_usd.toFixed(0)}/month - {stripePlans.pro.credits} credits per month (~{Math.round(stripePlans.pro.credits / 100)} videos)
                    </li>
                  )}
                  {stripePlans.ultimate && (
                    <li>
                      <strong>Premium:</strong> ${stripePlans.ultimate.price_monthly_usd.toFixed(0)}/month - {stripePlans.ultimate.credits} credits per month (~{Math.round(stripePlans.ultimate.credits / 100)} videos)
                    </li>
                  )}
                </>
              )}
            </ul>
            <p className="mt-4">
              Annual billing options may be available with discounted rates. All prices are in USD unless otherwise stated.
            </p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">4.2 Payment Processing</h3>
            <p>
              Payments are processed securely through Stripe, Inc. ("Stripe"). By subscribing, you agree to Stripe's Terms of Service and Privacy Policy. We do not store your full payment card information on our servers.
            </p>
            <p className="mt-4">
              <strong>Payment Authorization:</strong> By providing a payment method, you authorize us to charge that payment method for all fees associated with your subscription, including recurring charges.
            </p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">4.3 Billing and Renewal</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Subscriptions automatically renew at the end of each billing period unless canceled</li>
              <li>You will be charged the subscription fee on the renewal date</li>
              <li>Credits are allocated at the beginning of each billing period and do not roll over</li>
              <li>We reserve the right to change subscription prices with 30 days' notice</li>
              <li>Price changes will apply to your next billing cycle</li>
            </ul>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">4.4 Cancellation</h3>
            <p>
              You may cancel your subscription at any time through your account settings or by contacting us. Cancellation takes effect at the end of your current billing period. You will continue to have access to premium features until the end of the billing period for which you have already paid.
            </p>
            <p className="mt-4">
              <strong>No Refunds:</strong> Subscription fees are non-refundable, except as required by law or as explicitly stated in our Refund Policy (Section 4.5).
            </p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">4.5 Refund Policy</h3>
            <p>
              Due to the digital nature of our Service, we generally do not offer refunds for subscription fees. However, we may consider refund requests on a case-by-case basis in the following circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Technical issues preventing you from using the Service that we cannot resolve</li>
              <li>Duplicate charges or billing errors</li>
              <li>As required by applicable consumer protection laws</li>
            </ul>
            <p className="mt-4">
              To request a refund, please contact us at support@genanything.ai within 14 days of the charge. Refund requests are subject to review and approval at our sole discretion.
            </p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">4.6 Failed Payments</h3>
            <p>
              If a payment fails, we may suspend or terminate your subscription. We will attempt to notify you of failed payments and provide an opportunity to update your payment method. You are responsible for maintaining a valid payment method on file.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Credits and Usage</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Credits are consumed when you generate videos or use premium features</li>
              <li>Credit costs vary depending on the type of generation and quality selected</li>
              <li>Unused credits do not roll over to the next billing period</li>
              <li>We reserve the right to adjust credit costs and allocations</li>
              <li>Credits cannot be transferred, sold, or exchanged</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. User Content and Intellectual Property</h2>
            
            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">6.1 Your Content</h3>
            <p>
              You retain ownership of any content you upload or create using our Service ("User Content"). By uploading User Content, you grant us a worldwide, non-exclusive, royalty-free license to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Store, process, and display your User Content to provide the Service</li>
              <li>Use your User Content to generate videos and other outputs</li>
              <li>Back up and maintain your User Content</li>
            </ul>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">6.2 Generated Content</h3>
            <p>
              You own the videos and other content generated using our Service, subject to the rights of third parties (including AI model providers and content creators). However, you may not:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use generated content to violate any laws or third-party rights</li>
              <li>Claim that AI-generated content is human-created without disclosure</li>
              <li>Use generated content for illegal, harmful, or fraudulent purposes</li>
            </ul>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">6.3 Prohibited Content</h3>
            <p>You agree not to upload, generate, or share content that:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Is illegal, harmful, threatening, abusive, or discriminatory</li>
              <li>Infringes on intellectual property rights of others</li>
              <li>Contains explicit sexual content, violence, or hate speech</li>
              <li>Violates privacy rights of others</li>
              <li>Is spam, malware, or contains viruses</li>
              <li>Impersonates others or misrepresents your identity</li>
            </ul>
            <p className="mt-4">
              We reserve the right to remove any content that violates these Terms without notice.
            </p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">6.4 Our Intellectual Property</h3>
            <p>
              The Service, including its design, features, and functionality, is owned by GenAnything and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works of the Service without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Acceptable Use</h2>
            <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to the Service or related systems</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Reverse engineer, decompile, or disassemble the Service</li>
              <li>Use the Service to compete with us or build a similar service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Service Availability and Modifications</h2>
            <p>
              We strive to provide reliable service but do not guarantee uninterrupted or error-free operation. The Service may be temporarily unavailable due to maintenance, updates, or unforeseen circumstances.
            </p>
            <p className="mt-4">
              We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or without notice. We are not liable for any loss or damage resulting from such actions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. Third-Party Services</h2>
            <p>
              Our Service integrates with third-party services, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Stripe:</strong> Payment processing</li>
              <li><strong>Supabase:</strong> Authentication and database</li>
              <li><strong>AWS:</strong> Cloud storage</li>
              <li><strong>Replicate:</strong> AI model processing</li>
            </ul>
            <p className="mt-4">
              Your use of these third-party services is subject to their respective terms and privacy policies. We are not responsible for the practices of third-party services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. Disclaimers and Limitation of Liability</h2>
            
            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">10.1 Service Disclaimer</h3>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">10.2 Limitation of Liability</h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, GENANYTHING SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF THE SERVICE.
            </p>
            <p className="mt-4">
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">10.3 AI-Generated Content Disclaimer</h3>
            <p>
              AI-generated content may contain errors, inaccuracies, or unexpected results. We do not guarantee the accuracy, quality, or suitability of AI-generated content for any particular purpose. You are solely responsible for reviewing and verifying any content generated using our Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless GenAnything and its officers, directors, employees, and agents from and against any claims, damages, obligations, losses, liabilities, costs, or debt, and expenses (including attorney's fees) arising from:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Your User Content or generated content</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">12. Termination</h2>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including if you breach these Terms.
            </p>
            <p className="mt-4">
              Upon termination, your right to use the Service will cease immediately. You may cancel your account at any time through your account settings.
            </p>
            <p className="mt-4">
              Sections of these Terms that by their nature should survive termination shall survive, including Sections 6 (Intellectual Property), 10 (Limitation of Liability), 11 (Indemnification), and 13 (Dispute Resolution).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">13. Dispute Resolution</h2>
            
            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">13.1 Governing Law</h3>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of Wyoming, United States, without regard to its conflict of law provisions. GenAnything is incorporated in Sheridan, Wyoming.
            </p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">13.2 Dispute Resolution Process</h3>
            <p>
              If you have a dispute with us, you agree to first contact us at support@genanything.ai to attempt to resolve the dispute informally. If we cannot resolve the dispute within 60 days, you agree to resolve the dispute through binding arbitration in accordance with the rules of JAMS (Judicial Arbitration and Mediation Services). The arbitration shall be conducted in Sheridan, Wyoming, or at a location mutually agreed upon by the parties.
            </p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">13.3 Class Action Waiver</h3>
            <p>
              You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">14. Stripe-Specific Terms</h2>
            <p>
              When using Stripe for payment processing, you agree to comply with Stripe's Terms of Service and Acceptable Use Policy. You acknowledge that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Stripe processes payments on our behalf</li>
              <li>Stripe may collect and process your payment information</li>
              <li>We are not responsible for Stripe's services or any issues with payment processing</li>
              <li>Disputes related to payments may be subject to Stripe's dispute resolution process</li>
            </ul>
            <p className="mt-4">
              For more information, please review Stripe's Terms of Service at{' '}
              <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
                https://stripe.com/legal
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">15. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on this page and updating the "Last updated" date. Your continued use of the Service after such changes constitutes acceptance of the modified Terms.
            </p>
            <p className="mt-4">
              If you do not agree to the modified Terms, you must stop using the Service and cancel your subscription.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">16. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="mt-4 p-4 bg-white/5 rounded-lg">
              <p><strong>Email:</strong> support@genanything.ai</p>
              <p className="mt-2"><strong>Legal:</strong> legal@genanything.ai</p>
              <p className="mt-2"><strong>Address:</strong> GenAnything, Sheridan, Wyoming, United States</p>
              <p className="mt-2"><strong>Website:</strong> <a href="https://genanything.ai" className="text-blue-400 hover:text-blue-300 underline">https://genanything.ai</a></p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">17. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">18. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and GenAnything regarding the use of the Service and supersede all prior agreements and understandings.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-white/60 text-sm text-center">
            By using GenAnything, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>
        </div>
      </div>
    </div>
  );
}

